import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { Recorder } from '../media/MediaDrawer';
import { customFor, shrinkImage } from '../media/media';
import UsageLine from '../media/UsageLine';
import { useT } from '../i18n/i18n';
import { CORE_ID, type Layout } from './layout';
import { TYPE_LABEL, type WordType } from './vocabulary';

export const NEW_PAGE = '__new__';

export type MediaChange = { blob?: Blob; remove?: boolean };

export type ButtonValues = {
  word: string;
  picture: string;
  type: WordType;
  location: string;
  newPage: { label: string; picture: string } | null;
  hidden: boolean;
  photo: MediaChange;
  voice: MediaChange;
};

function useObjectUrl(blob: Blob | undefined) {
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return url;
}

export default function ButtonForm({
  mode, layout, initial, term, mediaAllowed, wordEditable, busy, onSubmit, onCancel, onRemove,
}: {
  mode: 'add' | 'edit';
  layout: Layout;
  initial: Omit<ButtonValues, 'photo' | 'voice' | 'newPage'>;
  term: string | null;
  mediaAllowed: boolean;
  wordEditable: boolean;
  busy: boolean;
  onSubmit: (v: ButtonValues) => void;
  onCancel?: () => void;
  onRemove?: () => void;
}) {
  const t = useT();
  const uid = useId();
  const [word, setWord] = useState(initial.word);
  const [picture, setPicture] = useState(initial.picture);
  const [type, setType] = useState<WordType>(initial.type);
  const [location, setLocation] = useState(initial.location);
  const [hidden, setHidden] = useState(initial.hidden);
  const [pageLabel, setPageLabel] = useState('');
  const [pagePicture, setPagePicture] = useState('');
  const [photo, setPhoto] = useState<MediaChange>({});
  const [voice, setVoice] = useState<MediaChange>({});
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => { setLocation(initial.location); }, [initial.location]);

  const existing = term ? customFor(term) : undefined;
  const photoPreview = useObjectUrl(photo.blob);
  const voicePreview = useObjectUrl(voice.blob);
  const shownPhoto = photo.remove ? null : photoPreview ?? existing?.image ?? null;
  const hasVoice = !voice.remove && (!!voice.blob || !!existing?.audio);

  async function choosePhoto(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      setPhoto({ blob: await shrinkImage(file) });
    } catch {
      setError(t('That picture could not be used. Try a JPEG or PNG photo.'));
    }
  }

  function listen() {
    const src = voicePreview ?? existing?.audio;
    if (!src) return;
    audioRef.current?.pause();
    audioRef.current = new Audio(src);
    void audioRef.current.play().catch(() => {});
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!word.trim()) return;
    if (location === NEW_PAGE && !pageLabel.trim()) { setError(t('Give the new page a name.')); return; }
    onSubmit({
      word: word.trim(),
      picture: picture.trim(),
      type,
      location,
      newPage: location === NEW_PAGE ? { label: pageLabel.trim(), picture: pagePicture.trim() } : null,
      hidden,
      photo,
      voice,
    });
    if (mode === 'add') {
      setWord(''); setPicture(''); setPhoto({}); setVoice({}); setPageLabel(''); setPagePicture('');
    }
  }

  return (
    <form className="tb-form" onSubmit={submit}>
      {error && <div className="error" role="alert">{error}</div>}
      <div className="tb-form-grid">
        <div className="field">
          <label htmlFor={`${uid}-word`}>{t('Word or phrase')}</label>
          <input id={`${uid}-word`} value={word} maxLength={40} disabled={!wordEditable} onChange={(e) => setWord(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor={`${uid}-pic`}>{mediaAllowed ? t('Picture: your own photo or an emoji') : t('Picture (an emoji)')}</label>
          <div className="tb-inline-field">
            {shownPhoto && <img src={shownPhoto} alt={t('Your photo')} className="tb-inline-thumb" />}
            <input
              id={`${uid}-pic`}
              value={picture}
              maxLength={8}
              placeholder={shownPhoto ? '' : '⭐'}
              aria-describedby={`${uid}-pic-hint`}
              onChange={(e) => setPicture(e.target.value)}
            />
            {mediaAllowed && (
              shownPhoto
                ? <button type="button" className="btn ghost tb-inline-btn" onClick={() => setPhoto({ remove: true })}>{t('Remove photo')}</button>
                : <button type="button" className="btn secondary tb-inline-btn" onClick={() => fileRef.current?.click()}>{t('Add photo')}</button>
            )}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { void choosePhoto(e.target.files?.[0]); e.target.value = ''; }} />
          </div>
          <span id={`${uid}-pic-hint`} className="field-hint">{shownPhoto ? t('The photo shows on the button. The emoji is used if the photo is removed.') : t('Type or paste an emoji.')}</span>
        </div>

        {mediaAllowed && (
          <div className="field">
            <span className="field-label" id={`${uid}-voice`}>{t('Your own voice (optional)')}</span>
            <div className="tb-inline-field tb-voice" role="group" aria-labelledby={`${uid}-voice`}>
              {recording ? (
                <Recorder onDone={(b) => { setVoice({ blob: b }); setRecording(false); }} onCancel={() => setRecording(false)} />
              ) : hasVoice ? (
                <>
                  <span className="tb-voice-state">{voice.blob ? t('New recording') : t('Recorded')}</span>
                  <button type="button" className="btn secondary tb-inline-btn" onClick={listen}>{t('Listen')}</button>
                  <button type="button" className="btn ghost tb-inline-btn" onClick={() => setRecording(true)}>{t('Record again')}</button>
                  <button type="button" className="btn ghost tb-inline-btn" onClick={() => setVoice({ remove: true })}>{t('Remove')}</button>
                </>
              ) : (
                <>
                  <span className="tb-voice-state muted">{t('Uses the chosen voice')}</span>
                  <button type="button" className="btn secondary tb-inline-btn" onClick={() => setRecording(true)}>{t('Record')}</button>
                </>
              )}
            </div>
          </div>
        )}

        <div className="field">
          <label htmlFor={`${uid}-where`}>{t('Put it on')}</label>
          <select id={`${uid}-where`} value={location} onChange={(e) => setLocation(e.target.value)}>
            <option value={CORE_ID}>{t('Main row (always on screen)')}</option>
            {layout.pages.map((p) => <option key={p.id} value={p.id}>{t('Page: {name}', { name: p.label })}</option>)}
            {mode === 'add' && <option value={NEW_PAGE}>{t('A new page…')}</option>}
          </select>
        </div>

        {location === NEW_PAGE && (
          <div className="field tb-newpage">
            <label htmlFor={`${uid}-page`}>{t('New page name')}</label>
            <div className="tb-inline-field">
              <input id={`${uid}-page`} value={pageLabel} maxLength={30} onChange={(e) => setPageLabel(e.target.value)} />
              <input aria-label={t('Page emoji')} className="tb-emoji-input" value={pagePicture} maxLength={8} placeholder="📁" onChange={(e) => setPagePicture(e.target.value)} />
            </div>
          </div>
        )}

        <div className="field">
          <label htmlFor={`${uid}-type`}>{t('Button colour')}</label>
          <select id={`${uid}-type`} value={type} onChange={(e) => setType(e.target.value as WordType)} aria-describedby={`${uid}-type-hint`}>
            {(Object.keys(TYPE_LABEL) as WordType[]).map((k) => <option key={k} value={k}>{t(TYPE_LABEL[k])}</option>)}
          </select>
          <span id={`${uid}-type-hint`} className="field-hint">{t('Talking boards colour buttons by kind of word, so the learner finds them faster.')}</span>
        </div>

        {mode === 'edit' && (
          <div className="field">
            <label className="check">
              <input type="checkbox" checked={!hidden} onChange={(e) => setHidden(!e.target.checked)} />
              {t('Show this button to the learner')}
            </label>
            <span className="field-hint">{t('A hidden button keeps its space, so the other buttons do not move.')}</span>
          </div>
        )}
      </div>

      {mediaAllowed && <UsageLine />}

      <div className="toolbar">
        <button type="submit" className="btn" disabled={busy || !word.trim()}>{mode === 'add' ? t('Add button') : t('Save button')}</button>
        {onCancel && <button type="button" className="btn ghost" onClick={onCancel}>{t('Close')}</button>}
        {onRemove && <button type="button" className="btn quiet-danger" onClick={onRemove}>{t('Remove button')}</button>}
      </div>
    </form>
  );
}
